import React from 'react';
import { Shield, Briefcase, HeadphonesIcon } from 'lucide-react';

interface Role {
  id: string;
  title: string;
  icon: any;
  description: string;
  color: string;
}

interface RolesInterfaceProps {
  showRoles: boolean;
  roles: Role[];
  activeRole: string | null;
  setActiveRole: (role: string | null) => void;
  setInputMessage: (message: string) => void;
  setShowRoles: (show: boolean) => void;
}

export const RolesInterface = ({
  showRoles,
  roles,
  activeRole,
  setActiveRole,
  setInputMessage,
  setShowRoles
}: RolesInterfaceProps) => {
  return (
    showRoles && (
      <div 
        className="bg-white rounded-lg shadow-lg border border-gray-200 mb-8 max-w-md mx-auto transition-all duration-200 ease-in-out"
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Select AI Role</h3>
            {activeRole && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Active: {roles.find(r => r.id === activeRole)?.title || 'Default'}
              </span>
            )}
          </div>
          <div className="space-y-3">
            {/* Default Role Option */}
            <button
              className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-start gap-4 hover:border-gray-400 hover:bg-gray-50 ${
                !activeRole 
                  ? 'border-gray-500 bg-gray-50 ring-1 ring-gray-500' 
                  : 'border-gray-200'
              }`}
              onClick={() => {
                setActiveRole(null);
                setInputMessage('');
                setShowRoles(false);
              }}
            >
              <div className="p-2 rounded-full bg-gray-100">
                <HeadphonesIcon className="w-5 h-5 text-gray-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">Default Assistant</div>
                <div className="text-sm text-gray-500">General financial assistant and guidance</div>
              </div>
              {!activeRole && (
                <div className="text-green-600 mt-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
            
            {roles.map((role) => (
              <button
                key={role.id}
                className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-start gap-4 hover:border-${role.color}-500 hover:bg-${role.color}-50 ${
                  activeRole === role.id 
                    ? `border-${role.color}-500 bg-${role.color}-50 ring-1 ring-${role.color}-500` 
                    : 'border-gray-200'
                }`}
                onClick={() => {
                  setActiveRole(role.id);
                  setInputMessage(`@${role.title}: `);
                  setShowRoles(false);
                }}
              >
                <div className={`p-2 rounded-full bg-${role.color}-100`}>
                  <role.icon className={`w-5 h-5 text-${role.color}-600`} />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{role.title}</div>
                  <div className="text-sm text-gray-500">{role.description}</div>
                </div>
                {activeRole === role.id && (
                  <div className="text-green-600 mt-2">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  );
};
